// SmartDateField.js
'use client';

import * as React from 'react';
import { InputBase, Typography } from '@mui/material';

/* ---------- shared helpers ---------- */
const pad = (n) => String(n).padStart(2, '0');
const toLocalDateTime = (d) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
const toLocalDate = (d) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const toLocalTimeHMS = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;

const parseLocalDateTime = (s) => {
    if (!s) return null;
    const [datePart, timePartRaw] = String(s).trim().replace(' ', 'T').split('T');
    if (!datePart || !timePartRaw) return null;
    const [y, m, d] = datePart.split('-').map(Number);
    const [hh = '0', mm = '0'] = timePartRaw.split(':');
    const H = Number(hh), M = Number(mm);
    if ([y, m, d, H, M].some(Number.isNaN)) return null;
    return new Date(y, (m || 1) - 1, d || 1, H || 0, M || 0, 0, 0);
};
const canonDateTime = (s) => {
    if (!s) return '';
    const [datePart, timePartRaw] = String(s).trim().replace(' ', 'T').split('T');
    return `${datePart}T${(timePartRaw || '00:00').slice(0, 5)}`;
};

const parseLocalDateOnly = (s) => {
    if (!s) return null;
    const [y, m, d] = String(s).split('-').map(Number);
    if ([y, m, d].some(Number.isNaN)) return null;
    return new Date(y, (m || 1) - 1, d || 1, 0, 0, 0, 0);
};
const canonDate = (s) => (s ? String(s).slice(0, 10) : '');

const parseLocalTimeOnly = (s) => {
    if (!s) return null;
    const [hh = '0', mm = '0', ss = '0'] = String(s).split(':');
    const H = Number(hh), M = Number(mm), S = Number(ss);
    if ([H, M, S].some(Number.isNaN)) return null;
    const d = new Date(); d.setHours(H || 0, M || 0, S || 0, 0);
    return d;
};
const canonTime = (s) => {
    if (!s) return '';
    const [hh = '00', mm = '00'] = String(s).split(':');
    return `${hh.padStart(2, '0')}:${mm.padStart(2, '0')}`;
};

const roundToStep = (d, minutes = 15, mode = 'ceil') => {
    if (minutes <= 1) return d;
    const m = d.getMinutes();
    const q = m / minutes;
    const rounded =
        mode === 'ceil' ? Math.ceil(q) * minutes :
            mode === 'floor' ? Math.floor(q) * minutes :
                Math.round(q) * minutes;
    d.setMinutes(rounded, 0, 0);
    return d;
};

/**
 * SmartDateField
 * Props:
 *  type: 'datetime' | 'date' | 'time'
 *  value: string
 *  setValue: (str) => void
 *  minDateTime/maxDateTime | minDate/maxDate | minTime/maxTime
 *  stepMinutes, rounding, showHint, fullWidth, sx, ...rest
 */
export default function SmartDateField({
    type = 'datetime',
    value,
    setValue,
    minDateTime,
    maxDateTime,
    minDate,
    maxDate,
    minTime,
    maxTime,
    stepMinutes = 15,
    rounding = 'ceil',
    showHint = true,
    fullWidth = true,
    sx,
    ...rest
}) {
    let belowMin = false, aboveMax = false;

    if (type === 'datetime') {
        if (minDateTime && value) belowMin = parseLocalDateTime(value) < parseLocalDateTime(minDateTime);
        if (maxDateTime && value) aboveMax = parseLocalDateTime(value) > parseLocalDateTime(maxDateTime);
    } else if (type === 'date') {
        if (minDate && value) belowMin = parseLocalDateOnly(value) < parseLocalDateOnly(minDate);
        if (maxDate && value) aboveMax = parseLocalDateOnly(value) > parseLocalDateOnly(maxDate);
    } else if (type === 'time') {
        const v = value && parseLocalTimeOnly(value);
        if (minTime && v) belowMin = v < parseLocalTimeOnly(minTime);
        if (maxTime && v) aboveMax = v > parseLocalTimeOnly(maxTime);
    }



    const onChangeDate = (yyyyMMdd) => {
        const orig = parseLocalDateOnly(yyyyMMdd);
        if (!orig) return setValue(value || '');
        const min = parseLocalDateOnly(minDate);
        const max = parseLocalDateOnly(maxDate);
        if (min && orig < min) return setValue(toLocalDate(min));
        if (max && orig > max) return setValue(toLocalDate(max));
        setValue(toLocalDate(orig));
    };

    const onChangeDateTime = (isoLocal) => {
        const orig = parseLocalDateTime(isoLocal);
        if (!orig) return setValue(value || '');
        const min = parseLocalDateTime(minDateTime);
        const max = parseLocalDateTime(maxDateTime);
        if (min && orig < min) return setValue(toLocalDateTime(min));
        if (max && orig > max) return setValue(toLocalDateTime(max));
        // ✅ use exact selected value
        setValue(toLocalDateTime(orig));
    };

    const onChangeTime = (hhmm) => {
        const orig = parseLocalTimeOnly(hhmm);
        if (!orig) return setValue(value || '');
        const min = parseLocalTimeOnly(minTime);
        const max = parseLocalTimeOnly(maxTime);
        if (min && orig < min) return setValue(toLocalTimeHMS(min));
        if (max && orig > max) return setValue(toLocalTimeHMS(max));
        // ✅ use exact selected value
        setValue(toLocalTimeHMS(orig));
    };


    if (type === 'date') {
        return (
            <>
                <InputBase
                    type="date"
                    value={value || ''}
                    onChange={(e) => onChangeDate(e.target.value)}
                    inputProps={{
                        min: canonDate(minDate),
                        max: canonDate(maxDate),
                        onInvalid: (e) => {
                            const minStr = canonDate(minDate);
                            const maxStr = canonDate(maxDate);
                            e.target.setCustomValidity(
                                minStr && maxStr
                                    ? `Pick a date between ${minStr} and ${maxStr}`
                                    : minStr ? `Pick a date ≥ ${minStr}` : maxStr ? `Pick a date ≤ ${maxStr}` : ''
                            );
                        },
                        onInput: (e) => e.target.setCustomValidity(''),
                    }}
                    sx={{ fontSize: 14, pr: 1, width: fullWidth ? '100%' : undefined, ...sx }}
                    {...rest}
                />
                {/* {showHint && (belowMin || aboveMax) && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                        {belowMin && minDate ? `Minimum allowed: ${canonDate(minDate)}` : null}
                        {aboveMax && maxDate ? `Maximum allowed: ${canonDate(maxDate)}` : null}
                    </Typography>
                )} */}
            </>
        );
    }

    if (type === 'time') {
        return (
            <>
                <InputBase
                    type="time"
                    value={value ? canonTime(value) : ''}
                    onChange={(e) => onChangeTime(e.target.value)}
                    inputProps={{
                        min: canonTime(minTime),
                        max: canonTime(maxTime),
                        step: 60,
                        onInvalid: (e) => {
                            const minStr = canonTime(minTime);
                            const maxStr = canonTime(maxTime);
                            e.target.setCustomValidity(
                                minStr && maxStr
                                    ? `Pick a time between ${minStr} and ${maxStr}`
                                    : minStr ? `Pick a time ≥ ${minStr}` : maxStr ? `Pick a time ≤ ${maxStr}` : ''
                            );
                        },
                        onInput: (e) => e.target.setCustomValidity(''),
                    }}
                    sx={{ fontSize: 14, pr: 1, width: fullWidth ? '100%' : undefined, ...sx }}
                    {...rest}
                />
                {/* {showHint && (belowMin || aboveMax) && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                        {belowMin && minTime ? `Minimum allowed: ${canonTime(minTime)}` : null}
                        {aboveMax && maxTime ? `Maximum allowed: ${canonTime(maxTime)}` : null}
                    </Typography>
                )} */}
            </>
        );
    }

    // default: datetime
    return (
        <>
            <InputBase
                type="datetime-local"
                value={value || ''}
                onChange={(e) => onChangeDateTime(e.target.value)}
                inputProps={{
                    min: canonDateTime(minDateTime),
                    max: canonDateTime(maxDateTime),
                    step: Math.max(60, stepMinutes * 60),
                    onInvalid: (e) => {
                        const minStr = canonDateTime(minDateTime)?.replace('T', ' ');
                        const maxStr = canonDateTime(maxDateTime)?.replace('T', ' ');
                        e.target.setCustomValidity(
                            minStr && maxStr
                                ? `Pick a time between ${minStr} and ${maxStr}`
                                : minStr ? `Pick a time ≥ ${minStr}` : maxStr ? `Pick a time ≤ ${maxStr}` : ''
                        );
                    },
                    onInput: (e) => e.target.setCustomValidity(''),
                }}
                sx={{ fontSize: 14, pr: 1, width: fullWidth ? '100%' : undefined, ...sx }}
                {...rest}
            />
            {/* {showHint && (belowMin || aboveMax) && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {belowMin && minDateTime ? `Minimum allowed: ${String(minDateTime).replace('T', ' ')}` : null}
                    {aboveMax && maxDateTime ? `Maximum allowed: ${String(maxDateTime).replace('T', ' ')}` : null}
                </Typography>
            )} */}
        </>
    );
}
